import type React from 'react';

import { useLayoutEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { observer } from 'mobx-react';
import { ErrorNoti } from '@pages/customer/common/modals/errorNoti';
import createEmployeeRouter from '@pages/customer/employee/employeeRouter';
import createManagerRouter from '@pages/customer/manager/managerRouter';
import { StoreProvider, useStore } from '@stores/RootStore';
import { getEnvConfig } from './configs/env';

import i18n from './locales/i18n';
import { ColorModeContext, useMode } from './themeCustomer';
import './initDayjs';

import './App.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/inter/600.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      cacheTime: 5 * 1000 * 60,
      staleTime: 4 * 1000 * 60,
    },
  },
});

export const App: React.FC = observer(() => {
  const [theme, colorMode] = useMode();

  const { uiStore } = useStore();
  const config = getEnvConfig();
  const { GOOGLE_CLIENT_ID, THEME_TYPE, INTRODUCTION_PAGE, ONLY_USE_LOGIN_PAGE, ENABLED_PRIVACY_POLICY } = config;

  const isManager = window.location.pathname.split('/')[1] === 'manager';

  const enabledIntroductionPage = INTRODUCTION_PAGE === 'true';
  const enabledOnlyUseLoginPage = ONLY_USE_LOGIN_PAGE === 'true';
  const enabledPrivacyPolicy = ENABLED_PRIVACY_POLICY === 'true';
  const employeeRouter = createEmployeeRouter({
    enabledIntroductionPage,
    enabledOnlyUseLoginPage,
    enabledPrivacyPolicy,
  });
  const managerRouter = createManagerRouter({
    enabledPrivacyPolicy,
  });
  // 개발 모드에서 환경변수 디버깅
  if (config.MODE === 'development') {
    console.log('🔍 All env vars:', Object.entries(config));
  }

  useLayoutEffect(() => {
    uiStore.setTypeUi(isManager ? 'IT_ADMIN_UI' : 'USER_UI');
  }, [isManager, uiStore]);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ColorModeContext.Provider value={{ ...colorMode, THEME_TYPE }}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              {/* Google OAuthProvider
              * NOTE: 환경변수에 GOOGLE_CLIENT_ID가 없을 경우 구글 로그인으로 인증 불가
                      SignIn.tsx에서 useGoogleLogin 훅을 사용하는데 clientId가 없으면 에러가 발생하기 때문에
                      환경변수에 GOOGLE_CLIENT_ID가 없을 경우 GoogleOAuthProvider에는 DUMMY_CLIENT_ID를 제공하도록 설정
              */}
              <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || 'DUMMY_CLIENT_ID'}>
                <StoreProvider>
                  <RouterProvider router={isManager ? managerRouter : employeeRouter} />
                  <ErrorNoti />
                </StoreProvider>
              </GoogleOAuthProvider>
            </ThemeProvider>
          </ColorModeContext.Provider>
        </LocalizationProvider>
      </I18nextProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
});
